import { ArtistsGrid } from "@components/artists_grid/artists_grid";
import { ArtistsSearchBox } from "@components/artists_search_box/artists_search_box";
import { Loader } from "@components/loader/loader";
import { ArtistsProvider } from "@contexts/artists_context";
import { useFetchArtists } from "@hooks/use_fetch_artists";
import { useFilterArtists } from "@hooks/use_filter_artists";
import * as types from "@root/types";
import { isEmpty } from "@services/helpers";
import { choose } from "@services/typed_helpers";
import React, { type FC, type ReactNode } from "react";

interface NoResultsProps {
  artists?: types.Artist[];
  query: string;
}

const NoResults: FC<NoResultsProps> = ({ artists, query }) => {
  const example = choose<string | null>(artists?.map((a) => a.fullName) || []);

  return (
    <div className="artists-page--no-results">
      <p>
        We couldn&apos;t find any artists whose name matches <em>{query}</em>.
      </p>
      <p>
        Maybe try a different spelling or fewer characters in the search
        {example ? (
          <>
            {" "}
            or something like <strong>{example}</strong>
          </>
        ) : null}
        .
      </p>
    </div>
  );
};

const ArtistsPage: FC = () => {
  const { artists: initialArtists, loading } = useFetchArtists();
  const { artists, query, updateQuery } = useFilterArtists(
    initialArtists || [],
  );

  let content: ReactNode;

  if (loading) {
    content = <Loader />;
  } else if (query && isEmpty(artists)) {
    content = <NoResults artists={initialArtists} query={query} />;
  } else {
    content = (
      <ArtistsProvider artists={artists}>
        <ArtistsGrid includeNames={true} />
      </ArtistsProvider>
    );
  }

  return (
    <>
      <header className="artists-page--header">
        <h1>Artists</h1>
        <ArtistsSearchBox onChange={updateQuery} />
      </header>
      <section className="artists">{content}</section>
    </>
  );
};

export { ArtistsPage };
